import { Graphics } from "pixi.js";
import { tokenColor, tokenShape } from "@talent/tasks";
import { COLOR, mix } from "./paint.js";

/**
 * A token — one shape in one colour — drawn on a rounded tile.
 *
 * Shared by the sequence game and spot-the-difference. They present tokens very differently, but
 * "what a token looks like" must be one answer: two copies would drift, and a shape that read
 * slightly differently between two games would be a difference a child could see and neither game
 * intended.
 *
 * **Colour is never the only cue anywhere it is used.** Both generators vary shape as well, so a
 * colour-blind child always has a complete route to the answer.
 */

/** The four token colours. Distinct in hue AND in lightness, so they survive a colour deficiency. */
export const TOKEN_COLORS = [COLOR.sunny, COLOR.watermelon, COLOR.turquoise, COLOR.grape];

export type TokenState = "idle" | "right" | "wrong" | "gap" | "found";

export function drawToken(
  g: Graphics,
  token: number | null,
  size: number,
  state: TokenState,
  accent: number,
  /**
   * Overrides the token's own colour.
   *
   * Used where the SHAPE is the message and the colour must say nothing — a sorting bin under the
   * shape rule, where a coloured icon would suggest the colour mattered when it is the property
   * being ignored.
   */
  overrideColor?: number,
): void {
  g.clear();

  if (state === "gap") {
    // An empty well, not a question mark: a glyph would be text (v2 §15), and a child who cannot
    // read still understands a hole in a row.
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.24).fill({ color: 0x000000, alpha: 0.22 });
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.24).stroke({
      width: 3,
      color: accent,
      alpha: 0.7,
    });
    return;
  }

  const panel =
    state === "right" || state === "found"
      ? mix(COLOR.correct, COLOR.stage, 0.55)
      : state === "wrong"
        ? mix(COLOR.wrong, COLOR.stage, 0.55)
        : mix(COLOR.stage, COLOR.card, 0.12);

  g.roundRect(-size / 2, -size / 2, size, size, size * 0.24).fill(panel);
  // A lit top edge, so the tile has volume rather than being a flat rectangle.
  g.roundRect(-size / 2 + size * 0.1, -size / 2 + size * 0.08, size * 0.8, size * 0.16, size * 0.1)
    .fill({ color: 0xffffff, alpha: 0.08 });

  if (token !== null) drawShape(g, token, size, overrideColor);

  if (state === "found") {
    // A ring, drawn on both panels at once, so a found difference stays visible while the child
    // looks for the rest.
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.24).stroke({
      width: 4,
      color: COLOR.correct,
    });
  }
}

/**
 * The six icons, drawn as paths.
 *
 * Recognisable objects rather than geometric primitives, because five variations on a polygon look
 * like a test and an apple looks like a game. They are drawn rather than loaded for the same reason
 * the creatures are: nothing to download on a metered connection, nothing to redraw when a game
 * changes, and the colour comes from the token so an icon and its tile can never disagree.
 *
 * **Only used where naming does not help.** Sequence completion and spot-the-difference keep their
 * stimulus on screen, so a child who thinks "apple, fish, apple, fish" gains nothing they were not
 * already being shown. The span games deliberately do NOT use these: a nameable sequence can be
 * rehearsed verbally, which is a different strategy for a different capacity, and it would quietly
 * turn a spatial working-memory measure into a partly verbal one (v2 §15).
 *
 * Silhouettes are distinct from one another on purpose. Spot-the-difference changes shape alone at
 * its easier levels, and two icons with similar outlines would make those levels a colour task.
 */
function drawShape(g: Graphics, token: number, size: number, overrideColor?: number): void {
  const color = overrideColor ?? TOKEN_COLORS[tokenColor(token)] ?? COLOR.sunny;
  const r = size * 0.3;
  const shine = { color: 0xffffff, alpha: 0.4 } as const;

  switch (tokenShape(token) % 6) {
    case 0: {
      // Apple: two lobes with a dip at the top, a stalk and a leaf.
      g.ellipse(-r * 0.32, r * 0.08, r * 0.62, r * 0.78).fill(color);
      g.ellipse(r * 0.32, r * 0.08, r * 0.62, r * 0.78).fill(color);
      g.rect(-r * 0.07, -r * 0.95, r * 0.14, r * 0.42).fill(mix(color, 0x000000, 0.45));
      g.ellipse(r * 0.34, -r * 0.78, r * 0.3, r * 0.16).fill(mix(color, 0x000000, 0.3));
      g.circle(-r * 0.36, -r * 0.24, r * 0.16).fill(shine);
      break;
    }
    case 1: {
      // Fish: a body and a tail fin.
      g.poly([r * 0.55, 0, r, -r * 0.55, r, r * 0.55]).fill(mix(color, 0x000000, 0.25));
      g.ellipse(-r * 0.12, 0, r * 0.72, r * 0.5).fill(color);
      g.circle(-r * 0.48, -r * 0.1, r * 0.11).fill(COLOR.stage);
      break;
    }
    case 2: {
      // Star.
      const pts: number[] = [];
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r * 0.44;
        pts.push(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      g.poly(pts).fill(color);
      g.circle(-r * 0.24, -r * 0.3, r * 0.16).fill(shine);
      break;
    }
    case 3: {
      // Heart: two lobes over a point.
      g.circle(-r * 0.4, -r * 0.28, r * 0.46).fill(color);
      g.circle(r * 0.4, -r * 0.28, r * 0.46).fill(color);
      g.poly([-r * 0.85, -r * 0.06, r * 0.85, -r * 0.06, 0, r * 0.92]).fill(color);
      g.circle(-r * 0.44, -r * 0.42, r * 0.15).fill(shine);
      break;
    }
    case 4: {
      // Flower: five petals round a centre.
      for (let i = 0; i < 5; i++) {
        const a = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        g.circle(Math.cos(a) * r * 0.56, Math.sin(a) * r * 0.56, r * 0.42).fill(color);
      }
      g.circle(0, 0, r * 0.34).fill(mix(color, 0xffffff, 0.55));
      break;
    }
    default: {
      // Crescent moon: a disc with a bite taken out, which is what makes it a moon and not a ball.
      g.circle(0, 0, r).fill(color);
      g.circle(r * 0.42, -r * 0.18, r * 0.82).fill(COLOR.stage);
      g.circle(-r * 0.44, r * 0.2, r * 0.13).fill(shine);
      break;
    }
  }
}
